// src/dashboard/pages/teacher/Palmares/PalmaresDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { usePalmaresDashboard } from "./hooks/usePalmaresDashboard";

import DashboardPalmaresHeader from "./components/DashboardPalmaresHeader";
import PalmaresFiltersCard from "./components/PalmaresFiltersCard";
import PalmaresKpiCards from "./components/PalmaresKpiCards";
import PalmaresDataTable from "./components/PalmaresDataTable";
import PalmaresMobileCards from "./components/PalmaresMobileCards";
import PalmaresEmptyState from "./components/PalmaresEmptyState";
import AddPalmaresDrawer from "./components/AddPalmaresDrawer";
import EditPalmaresDrawer from "./components/EditPalmaresDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Palmarès
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux enseignants et administrateurs du
              club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PalmaresDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const {
    palmares,
    filteredPalmares,
    adherents,
    events,
    palmaresMemberOptions,
    seasonOptions,
    activeSeason,
    stats,

    filters,
    setFilters,

    loading,
    saving,
    deletingId,
    togglingId,

    addDrawerOpen,
    editDrawerOpen,
    currentPalmares,

    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,

    handleCreatePalmares,
    handleUpdatePalmares,
    handleDeletePalmares,
    handleToggleIsActive,
    refreshPalmares,
  } = usePalmaresDashboard();

  if (![1, 3].includes(roleId)) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardPalmaresHeader />

        <PalmaresFiltersCard
          filters={filters}
          setFilters={setFilters}
          seasonOptions={seasonOptions}
          memberOptions={palmaresMemberOptions}
          activeSeason={activeSeason}
          loading={loading}
          onAdd={openAddDrawer}
          onRefresh={refreshPalmares}
        />

        <PalmaresKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Liste des palmarès
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Résultats, classements et distinctions
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Suivez les résultats des adhérents, rattachez-les à un
                  événement et choisissez ceux qui doivent apparaître sur la
                  page d’accueil.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Palmarès affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredPalmares.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {palmares.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-20 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredPalmares.length === 0 ? (
              <PalmaresEmptyState onAdd={openAddDrawer} />
            ) : (
              <div className="mt-5">
                <PalmaresDataTable
                  items={filteredPalmares}
                  events={events}
                  onEdit={openEditDrawer}
                  onDelete={handleDeletePalmares}
                  onToggleIsActive={handleToggleIsActive}
                  deletingId={deletingId}
                  togglingId={togglingId}
                />

                <PalmaresMobileCards
                  items={filteredPalmares}
                  events={events}
                  onEdit={openEditDrawer}
                  onDelete={handleDeletePalmares}
                  onToggleIsActive={handleToggleIsActive}
                  deletingId={deletingId}
                  togglingId={togglingId}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <AddPalmaresDrawer
        isOpen={addDrawerOpen}
        onClose={closeAddDrawer}
        onSubmit={handleCreatePalmares}
        saving={saving}
        adherents={adherents}
        events={events}
      />

      <EditPalmaresDrawer
        isOpen={editDrawerOpen}
        onClose={closeEditDrawer}
        palmares={currentPalmares}
        onSubmit={handleUpdatePalmares}
        onDelete={handleDeletePalmares}
        saving={saving}
        deletingId={deletingId}
        events={events}
      />

      <ToastContainer />
    </section>
  );
}
